import React, { useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import courseUploadService from "../../../services/courseUpload.service";

const CourseUpload = () => {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = user.client_id;

  const collegeId = user.college_id;

  const [file, setFile] = useState(null);

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [importing, setImporting] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "info",
    message: "",
  });
  const downloadTemplate = () => {
    const headers = [
      "program",
      "branch",
      "regulation",
      "year_number",
      "semester_number",
      "course_order",
      "course_code",
      "short_code",
      "course_level",
      "course_name",
      "course_type",
      "course_category",
      "elective_number",
      "credits",
      "total_minimum",
      "internal_maximum",
      "internal_minimum",
      "external_minimum",
      "external_maximum",
      "course_part",
      "course_group",
      "print_order",
      "mid_order",
    ];

    const sampleData = [
      {
        program: "B.Sc.",
        branch: "CS",
        regulation: "R23",
        year_number: 1,
        semester_number: 1,
        course_order: 1,
        print_order: 1,
        mid_order: 0,
        course_code: "23CS101",
        short_code: "PPS",
        course_level: "MINOR",
        course_name: "Programming for Problem Solving",
        course_type: "T/P",
        course_category: "O/E",
        elective_number: "",
        credits: 4,
        total_minimum: 40,
        internal_maximum: 30,
        internal_minimum: 12,
        external_minimum: 28,
        external_maximum: 70,
        course_part: "PART-A",
        course_group: "GROUP-1",
      },
    ];

    const csvRows = [
      headers.join(","),
      ...sampleData.map((row) => headers.map((h) => `"${row[h] ?? ""}"`).join(",")),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "ExamCourseUploadFormat.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };
  const showMessage = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };
  // ---------------------------------------------------------
  // FILE CHANGE
  // ---------------------------------------------------------

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf(".")).toLowerCase();

    if (![".csv", ".xls", ".xlsx"].includes(extension)) {
      showMessage("Please select CSV, XLS or XLSX file.", "error");

      event.target.value = "";
      return;
    }

    setFile(selectedFile);
    setRows([]);
  };

  // ---------------------------------------------------------
  // VALIDATE
  // ---------------------------------------------------------

  const handleValidate = async () => {
    if (!file) {
      showMessage("Please select a CSV/Excel file.", "warning");
      return;
    }

    if (!clientId || !collegeId) {
      showMessage("Client or college information is missing.", "error");
      return;
    }

    try {
      setLoading(true);

      const result = await courseUploadService.validateCourseUpload({
        clientId,
        collegeId,
        file,
      });

      if (!result.success) {
        throw new Error(result.message);
      }

      setRows(result.rows || []);

      setPreviewOpen(true);

      showMessage(
        `Validation completed. ${result.valid_rows} valid and ${result.invalid_rows} invalid rows.`,
        result.invalid_rows > 0 ? "warning" : "success"
      );
    } catch (error) {
      console.error(error);

      showMessage(error.response?.data?.message || error.message || "Validation failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // IMPORT
  // ---------------------------------------------------------

  const handleImport = async () => {
    const invalidRows = rows.filter((row) => !row.valid);

    if (invalidRows.length > 0) {
      showMessage("Invalid rows exist. Correct the file and upload again.", "error");
      return;
    }

    if (!rows.length) {
      showMessage("There are no rows to import.", "warning");
      return;
    }

    try {
      setImporting(true);

      const result = await courseUploadService.importCourseUpload({
        clientId,
        collegeId,
        rows,
      });

      if (!result.success) {
        throw new Error(result.message);
      }

      showMessage(result.message || "Courses imported successfully.", "success");

      setPreviewOpen(false);
      setRows([]);
      setFile(null);

      const fileInput = document.getElementById("course-upload-file");

      if (fileInput) {
        fileInput.value = "";
      }
    } catch (error) {
      console.error(error);

      showMessage(error.response?.data?.message || error.message || "Import failed.", "error");
    } finally {
      setImporting(false);
    }
  };

  // ---------------------------------------------------------
  // COUNTS
  // ---------------------------------------------------------

  const validCount = useMemo(() => rows.filter((row) => row.valid).length, [rows]);

  const invalidCount = useMemo(() => rows.filter((row) => !row.valid).length, [rows]);

  // ---------------------------------------------------------
  // GRID COLUMNS
  // ---------------------------------------------------------

  const columns = useMemo(
    () => [
      {
        field: "row_number",
        headerName: "Row",
        width: 70,
      },

      {
        field: "valid",
        headerName: "Validation",
        width: 120,
        renderCell: (params) =>
          params.value ? (
            <Chip label="VALID" color="success" size="small" />
          ) : (
            <Chip label="ERROR" color="error" size="small" />
          ),
      },
      {
        field: "errors",
        headerName: "Errors",
        flex: 1,
        minWidth: 500,
        renderCell: (params) => (
          <Box
            sx={{
              py: 1,
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.5,
              fontSize: 16,
              color: "error.main",
            }}
          >
            {(params.value || []).map((error, index) => (
              <div key={index}>• {error}</div>
            ))}
          </Box>
        ),
      },
      {
        field: "program_name",
        headerName: "Program",
        width: 180,
      },

      {
        field: "branch_name",
        headerName: "Branch",
        width: 180,
      },

      {
        field: "regulation_name",
        headerName: "Regulation",
        width: 160,
      },

      {
        field: "regulation_code",
        headerName: "Regulation Code",
        width: 140,
      },

      {
        field: "admittedbatches",
        headerName: "Admitted Batches",
        width: 300,
        sortable: false,
        renderCell: (params) => {
          const batches = params.row.admittedbatches || [];

          if (!batches.length) {
            return "-";
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                py: 0.5,
              }}
            >
              {batches.map((batch) => (
                <Chip
                  key={batch.admittedbatch_id}
                  label={`${batch.admittedbatch} (${batch.admittedbatch_id})`}
                  size="small"
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    fontWeight: 600,
                    "& .MuiChip-label": {
                      color: "#fff",
                    },
                  }}
                />
              ))}
            </Box>
          );
        },
      },

      {
        field: "year_number",
        headerName: "Year",
        width: 80,
      },

      {
        field: "semester_number",
        headerName: "Semester",
        width: 100,
      },

      {
        field: "course_order",
        headerName: "Course Order",
        width: 120,
      },
      {
        field: "print_order",
        headerName: "Print Order",
        width: 120,
      },
      {
        field: "mid_order",
        headerName: "MID Order",
        width: 120,
      },
      {
        field: "course_code",
        headerName: "Course Code",
        width: 130,
      },

      {
        field: "short_code",
        headerName: "Short Code",
        width: 120,
      },

      {
        field: "course_name",
        headerName: "Course Name",
        width: 250,
      },

      {
        field: "course_type",
        headerName: "Course Type",
        width: 130,
      },

      {
        field: "course_category",
        headerName: "Category",
        width: 130,
      },

      {
        field: "credits",
        headerName: "Credits",
        width: 90,
      },

      {
        field: "total_minimum",
        headerName: "Total Min",
        width: 100,
      },

      {
        field: "internal_maximum",
        headerName: "Internal Max",
        width: 110,
      },

      {
        field: "internal_minimum",
        headerName: "Internal Min",
        width: 110,
      },

      {
        field: "external_minimum",
        headerName: "External Min",
        width: 110,
      },

      {
        field: "external_maximum",
        headerName: "External Max",
        width: 110,
      },
    ],
    []
  );

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Typography
            variant="h5"
            fontWeight={600}
            gutterBottom
            sx={{
              fontSize: {
                xs: "1rem",
                sm: "1.2rem",
                md: "1.4rem",
                lg: "1.5rem",
              },
            }}
          >
            UPLOAD COLLEGE WISE COURSE MASTER
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: {
                xs: "0.8rem",
                sm: "0.9rem",
                md: "0.95rem",
              },
            }}
          >
            Upload CSV, XLS or XLSX. Program, Branch and Regulation can be supplied as IDs or Names.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                  sx={{
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                  }}
                >
                  Download Format
                </Button>

                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: "#2513ea",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#1565c0",
                    },
                  }}
                >
                  Choose File
                  <input
                    id="course-upload-file"
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileChange}
                    hidden
                  />
                </Button>

                <Typography
                  sx={{
                    flexGrow: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                    fontSize: "0.9rem",
                    color: file ? "#333" : "#757575",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={file?.name}
                >
                  {file ? file.name : "No file selected"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} lg={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleValidate}
                disabled={loading || !file}
                sx={{
                  minHeight: 42,
                  backgroundColor: "#1976d2",
                  color: "#ffffff",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                  "&:disabled": {
                    backgroundColor: "#90caf9",
                    color: "#ffffff",
                  },
                }}
              >
                {loading ? "Validating..." : "Validate & Preview"}
              </Button>
            </Grid>
          </Grid>

          {file && (
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                wordBreak: "break-word",
              }}
            >
              Selected file: <strong>{file.name}</strong>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ===================================================
          PREVIEW DIALOG
          =================================================== */}

      <Dialog
        open={previewOpen}
        onClose={(event, reason) => {
          // Prevent closing by clicking outside or pressing Escape
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }

          if (!importing) {
            setPreviewOpen(false);
          }
        }}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle
          sx={{
            position: "relative",
            pr: 6,
            fontWeight: 600,
          }}
        >
          Exam Course Validation
          {/* Top-right Close Button */}
          <Button
            onClick={() => {
              if (!importing) {
                setPreviewOpen(false);
              }
            }}
            disabled={importing}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              minWidth: 40,
              width: 40,
              height: 40,
              padding: 0,
              fontSize: "26px",
              lineHeight: 1,
              color: "#ffffff",
              backgroundColor: "#ea0d5a",
              borderRadius: "50%",

              "&:hover": {
                backgroundColor: "#e21254",
              },

              "&:disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            ×
          </Button>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item>
              <Chip label={`Total: ${rows.length}`} />
            </Grid>

            <Grid item>
              <Chip
                label={`Valid: ${validCount}`}
                color="success"
                sx={{
                  color: "#ffffff",
                  fontWeight: 600,
                  "& .MuiChip-label": {
                    color: "#ffffff",
                  },
                }}
              />
            </Grid>

            <Grid item>
              <Chip label={`Invalid: ${invalidCount}`} color={invalidCount ? "error" : "default"} />
            </Grid>
          </Grid>

          <Box
            sx={{
              height: 600,
              width: "100%",
            }}
          >
            <DataGrid
              rows={rows.map((row, index) => ({
                ...row,
                id: `${row.row_number}_${index}`,
              }))}
              columns={columns}
              pageSize={25}
              rowsPerPageOptions={[25, 50, 100]}
              disableSelectionOnClick
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiDataGrid-cell": {
                  fontSize: "0.85rem",
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              if (!importing) {
                setPreviewOpen(false);
              }
            }}
            disabled={importing}
            variant="contained"
            sx={{
              backgroundColor: "#d21919",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#d7144b",
              },
              "&:disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || rows.length === 0 || invalidCount > 0}
            sx={{
              backgroundColor: "#1976d2",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              "&:disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            {importing ? "Importing..." : `Import ${validCount} Course(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          top: "20px !important",
          right: "20px !important",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          variant="filled"
          sx={{
            backgroundColor: "#e01919",
            color: "#ffffff",
            minWidth: "350px",
            fontSize: "0.95rem",
            fontWeight: 500,

            "& .MuiAlert-icon": {
              color: "#ffffff",
            },

            "& .MuiAlert-action": {
              color: "#ffffff",
            },

            "& .MuiIconButton-root": {
              color: "#ffffff",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseUpload;
